// ─── DRAFT MEMORY — shared character-lookup helpers ───────────────────────
// Used by DraftAssistant (DMs / RP), ChannelView's inline draft generator,
// and RouteDirector. Before the AI builds a draft, we scan the assembled
// input (recent messages + idea/revise text + scene partners) for character
// mentions, then pull each one's merged profile, facts ledger, and
// per-player memory and inject them as a CHARACTERS REFERENCED section.
//
// SECRECY (audit 2026-08-03 P0-1/P2-11): this module used to print raw
// `relationships`/`backstory`/`species` — which is how a draft prompt could
// casually learn "Alyce is Moros" or "Nicole is Lachesis reincarnated".
// Dossiers are now GATED: a character with a `publicIdentity` cover shows
// the cover instead of their private fields, and every field that does
// render is scrubbed of identity-reveal sentences. `controlledBy` is gone
// entirely — which human plays whom is an out-of-fiction fact no draft
// needs.
//
// All lookups are best-effort: any error returns an empty string so the
// draft never breaks because of a memory hiccup.

import { CHARACTERS } from './characters.js';
import { buildKnownPlaces, buildLivingLore, buildKnownGroups, loadCharacterOverrides, mergeCharWithOverrides, scrubIdentityReveals as _scrub } from './characterIdentity.js';
import { loadEncyclopediaRows } from './encyclopediaLive.js';
import { getCharacterFacts } from './characterFacts.js';
import { getCharacterMemory } from './conversationMemory.js';
import { getCharacterStoryArc, formatStoryArcForPrompt } from './storyMemory.js';
import { getPriorFeedback, formatPriorFeedbackForPrompt } from './discordContext.js';
import { getSceneSummary, formatSceneSummaryForPrompt } from './sceneSummary.js';
import { getRollingSummary, formatSummaryForPrompt } from './rollingSummary.js';
import { buildSpeciesLoreSection } from './speciesLore.js';

// ─── CHARACTER MENTION DETECTION ──────────────────────────────────────────
// Build a list of {token, characterId} pairs used to scan free text for
// character mentions. Lazily initialized + cached on the module.
let MENTION_TABLE = null;
function getMentionTable() {
  if (MENTION_TABLE) return MENTION_TABLE;
  const rows = [];
  for (const c of CHARACTERS) {
    if (!c?.id || !c?.name) continue;
    const tokens = new Set();
    tokens.add(c.name);                                  // "Ford Steele"
    const first = c.name.split(/\s+/)[0];
    if (first && first.length >= 3) tokens.add(first);   // "Ford"
    if (c.fullName) tokens.add(c.fullName);              // "Princess Kya …"
    for (const t of tokens) {
      const cleaned = String(t).trim();
      if (cleaned.length < 3) continue;
      rows.push({ token: cleaned, id: c.id });
    }
  }
  // Longest tokens first so "Ford Steele" matches before "Ford".
  rows.sort((a, b) => b.token.length - a.token.length);
  MENTION_TABLE = rows;
  return MENTION_TABLE;
}

function escapeRegex(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

/**
 * Scan free text and return the set of character ids mentioned (best-effort
 * substring match with word boundaries).
 *
 * @param {string} text  the concatenated input text to scan
 * @param {Set<string>} excludeIds  character ids to skip (e.g. the player's own)
 * @returns {string[]}
 */
export function detectMentionedCharacterIds(text, excludeIds = new Set()) {
  if (!text) return [];
  const found = new Set();
  for (const { token, id } of getMentionTable()) {
    if (excludeIds.has(id) || found.has(id)) continue;
    const re = new RegExp(`\\b${escapeRegex(token)}\\b`, 'i');
    if (re.test(text)) found.add(id);
  }
  return [...found];
}

// ─── SECRET SCRUBBING ─────────────────────────────────────────────────────
// The scrubber lives in characterIdentity.js now (audit Wave E: seven leak
// sites share one gate). Re-exported here so existing imports/tests hold.
export { scrubIdentityReveals } from './characterIdentity.js';
const scrubIdentityReveals = _scrub;

/** Compact dossier for one merged character. Tuned for tokens, not display. */
function formatCharacterDossier(char, facts, memoryFacts) {
  const parts = [];
  const gated = !!char.publicIdentity;
  // Species can itself be a reveal ("God in disguise — secretly Moros").
  const scrubbedSpecies = scrubIdentityReveals(String(char.species || ''));
  const species = gated && scrubbedSpecies !== String(char.species || '').trim() ? null : scrubbedSpecies;
  parts.push(`▸ ${char.name}${species ? ` (${species})` : ''}`);
  const trim = (s, n) => {
    if (!s) return '';
    const flat = String(s).replace(/\s+/g, ' ').trim();
    return flat.length > n ? flat.slice(0, n - 1) + '…' : flat;
  };
  if (gated) {
    // The cover IS what the world knows. Private relationships/backstory
    // stay out entirely — this is the whole point of publicIdentity.
    parts.push(`  known as: ${trim(char.publicIdentity, 300)}`);
  } else if (char.fullName && char.fullName !== char.name
    && scrubIdentityReveals(char.fullName) === String(char.fullName).trim()) {
    // Shown only when the full name survives the scrubber untouched — a name
    // like "Atropos / Morta — reincarnated as Haley" IS the secret.
    parts.push(`  full name: ${trim(char.fullName, 120)}`);
  }
  if (char.personality) parts.push(`  personality: ${trim(scrubIdentityReveals(char.personality), 400)}`);
  if (char.speechPattern || char.speech_pattern) parts.push(`  speech: ${trim(char.speechPattern || char.speech_pattern, 350)}`);
  if (!gated && char.relationships) parts.push(`  relationships: ${trim(scrubIdentityReveals(char.relationships), 400)}`);
  if (!gated && char.backstory) parts.push(`  backstory: ${trim(scrubIdentityReveals(char.backstory), 400)}`);
  const quotes = (char.exampleQuotes || char.example_quotes || []).slice(0, 3);
  if (quotes.length) parts.push(`  in their own voice:\n${quotes.map(q => `    "${trim(q, 200)}"`).join('\n')}`);
  if (facts && facts.length > 0) {
    const lines = facts.slice(0, 8).map(f => {
      const lock = f.locked ? ' 🔒' : '';
      return `    • ${f.fact_key}: ${trim(f.fact_value, 140)}${lock}`;
    });
    parts.push(`  established facts (canon for this player):\n${lines.join('\n')}`);
  }
  if (memoryFacts && memoryFacts.length > 0) {
    // dm_conversation_facts rows: { fact, category, importance }
    const lines = memoryFacts.slice(0, 6).map(m =>
      `    • ${trim(typeof m === 'string' ? m : m.fact, 140)}`);
    parts.push(`  what they remember with this player:\n${lines.join('\n')}`);
  }
  return parts.join('\n');
}

/**
 * Build the "CHARACTERS REFERENCED" prompt section by scanning the assembled
 * input text for character mentions (plus any explicitly included cast ids),
 * then pulling each one's merged profile, facts ledger, and per-player
 * conversation memory. Dossiers are secrecy-gated (see header).
 *
 * @param {object} args
 * @param {string} args.searchText
 * @param {string} [args.userId]
 * @param {Set<string>} [args.excludeIds]
 * @param {string[]} [args.includeIds]  cast ids that are ALWAYS included
 *   (the chat's own cast), ahead of detected mentions
 * @param {number} [args.maxChars]
 * @param {boolean} [args.withSpeciesLore]  append the species-canon block for
 *   the included cast's species
 * @returns {Promise<string>}  the section text (incl. leading newlines) or ''
 */
export async function buildCharacterContextSection({ searchText, userId, excludeIds, includeIds = [], maxChars = 6, withSpeciesLore = false }) {
  try {
    const exclude = excludeIds || new Set();
    const detected = detectMentionedCharacterIds(searchText, exclude);
    const ids = [...new Set([...(includeIds || []).filter(id => id && !exclude.has(id)), ...detected])];
    if (ids.length === 0) return '';
    const limited = ids.slice(0, maxChars);
    let overrides = {};
    try { overrides = await loadCharacterOverrides(); } catch {}
    // 2026-10-05 (audit 4.5): the dossier never loaded the living encyclopedia,
    // so a draft set in the Pack House got none of the room-by-room detail the
    // character's own prompt would have. Same loader, same three builders.
    let encRows = null;
    try { encRows = await loadEncyclopediaRows(); } catch { /* additive */ }
    const factsByChar = {};
    const memoryByChar = {};
    if (userId) {
      await Promise.all(limited.map(async id => {
        try { factsByChar[id] = await getCharacterFacts(id, userId, 12); } catch { factsByChar[id] = []; }
        try { memoryByChar[id] = await getCharacterMemory(id, userId); } catch { memoryByChar[id] = []; }
      }));
    }
    const dossiers = [];
    const speciesStrings = [];
    for (const id of limited) {
      const base = CHARACTERS.find(c => c.id === id);
      if (!base) continue;
      const merged = mergeCharWithOverrides(base, overrides) || base;
      if (merged.species) speciesStrings.push(merged.species);
      let dossier = formatCharacterDossier(merged, factsByChar[id] || [], memoryByChar[id] || []);
      try {
        const world = [buildKnownPlaces(merged, encRows), buildLivingLore(merged, encRows), buildKnownGroups(merged, encRows)]
          .filter(Boolean).join('\n');
        if (world) dossier += `\n[The world as ${merged.name} knows it]\n${world}`;
      } catch { /* additive */ }
      dossiers.push(dossier);
    }
    if (!dossiers.length) return '';
    let out = `\n\n═══ CHARACTERS REFERENCED IN THIS SCENE ═══\nThese characters are in or mentioned around this scene. Use their canon — their voice, their species, their established relationships and facts — when the draft refers to them. Do NOT invent traits or facts that contradict the dossier. If the dossier doesn't cover something, prefer silence over fabrication.\n\n${dossiers.join('\n\n')}\n═══════════════════════════════════════════\n`;
    if (withSpeciesLore && speciesStrings.length) {
      try { out += await buildSpeciesLoreSection(speciesStrings); } catch {}
    }
    return out;
  } catch (err) {
    // External memory is best-effort — never fail the draft.
    console.warn('[draftMemory] buildCharacterContextSection failed (non-fatal):', err?.message || err);
    return '';
  }
}

/**
 * THE MEMORY OF THE CHAT THE TOOL IS MOUNTED IN (Haley, 2026-08-03: "and the
 * memory of whatever chat I'm using the tools in"). Scope-appropriate:
 *   dm      → that conversation's story arc + prior feedback + rolling summary
 *   channel → the CURRENT SCENE's summary (scene-scoped — never the other
 *             scene's), falling back to the channel rolling summary only when
 *             the channel has no scenes
 *   rp      → the story's rolling summary
 * Every piece is best-effort and clearly labeled as the chat's own memory.
 */
export async function buildChatMemorySection({ scope, scopeId, characterId = null, channelId = null, sceneTag = null, userId = null, playerLabel = 'the player' } = {}) {
  const parts = [];
  try {
    const charName = characterId
      ? (CHARACTERS.find(c => c.id === characterId)?.name || 'the character')
      : 'the character';
    if (scope === 'dm' && characterId && userId) {
      try {
        const arc = await getCharacterStoryArc(characterId, userId);
        const t = formatStoryArcForPrompt(arc, charName, playerLabel);
        if (t) parts.push(t);
      } catch {}
      try {
        const fb = await getPriorFeedback(characterId, userId);
        const t = formatPriorFeedbackForPrompt(fb, charName);
        if (t) parts.push(t);
      } catch {}
      try {
        const s = await getRollingSummary('dm', scopeId);
        const t = formatSummaryForPrompt(s, charName, playerLabel);
        if (t) parts.push(t);
      } catch {}
    } else if (scope === 'channel' && (channelId || scopeId)) {
      const chId = channelId || scopeId;
      if (sceneTag) {
        try {
          const row = await getSceneSummary(chId, sceneTag);
          const t = formatSceneSummaryForPrompt(row, charName, playerLabel);
          if (t) parts.push(t);
        } catch {}
      } else {
        // Only when the channel has NO scene dimension — a scene-tagged
        // channel must never fall back to the channel-wide summary (that is
        // the cross-scene leak the scene wall exists to stop).
        try {
          const s = await getRollingSummary('channel', chId);
          const t = formatSummaryForPrompt(s, charName, playerLabel);
          if (t) parts.push(t);
        } catch {}
      }
    } else if (scope === 'rp' && scopeId) {
      try {
        const s = await getRollingSummary('story', scopeId);
        const t = formatSummaryForPrompt(s, charName, playerLabel);
        if (t) parts.push(t);
      } catch {}
    }
  } catch {}
  if (!parts.length) return '';
  return `\n\n═══ THIS CHAT'S OWN MEMORY — what has actually happened here ═══\n${parts.join('\n\n')}\n═══════════════════════════════════════════`;
}
